import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageContainer } from '@/components/layout/PageContainer';
import { Toast } from '@/components/common/Toast';
import { useSellerSignupData } from '@/hooks/useSellerSignupData';

export function SellerBusinessInfoPage() {
  const navigate = useNavigate();
  const { businessInfo, agreements, updateBusinessInfo, updateAgreements } = useSellerSignupData();

  const [formData, setFormData] = useState({
    companyName: businessInfo.companyName || '',
    representative: businessInfo.representative || '',
    businessNumber: businessInfo.businessNumber || '',
    bankName: businessInfo.bankName || '',
    accountNumber: businessInfo.accountNumber || '',
    accountHolder: businessInfo.accountHolder || ''
  });

  const [agreementData, setAgreementData] = useState({
    terms: agreements.terms || false,
    privacy: agreements.privacy || false,
    marketing: agreements.marketing || false
  });

  const [toast, setToast] = useState<{ show: boolean; message: string }>({ show: false, message: '' });

  const handleBack = () => {
    navigate(-1);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // localStorage에 자동 저장
    updateBusinessInfo({ [field]: value });
  };

  const handleAgreementChange = (field: string, checked: boolean) => {
    setAgreementData(prev => ({
      ...prev,
      [field]: checked
    }));
    
    // localStorage에 자동 저장
    updateAgreements({ [field]: checked });
  };

  const handleAllAgreementChange = (checked: boolean) => {
    const newAgreements = {
      terms: checked,
      privacy: checked,
      marketing: checked
    };
    setAgreementData(newAgreements);
    updateAgreements(newAgreements);
  };

  const isFormValid = () => {
    return formData.companyName &&
           formData.representative &&
           formData.businessNumber &&
           formData.bankName &&
           formData.accountNumber &&
           formData.accountHolder &&
           agreementData.terms &&
           agreementData.privacy;
  };

  const handleNext = () => {
    if (!isFormValid()) {
      setToast({ show: true, message: '모든 필수 항목을 입력하고 약관에 동의해주세요' });
      return;
    }

    // 다음 단계로 이동 - 매장 정보 입력
    // localStorage를 사용하므로 location.state 대신 직접 이동
    navigate('/signup/seller/store');
  };

  return (
    <div className="min-h-screen bg-white">
      <PageContainer>
        {/* 헤더 */}
        <div className="flex items-center justify-center relative mb-8">
          <button
            onClick={handleBack}
            className="absolute left-0 p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <img
              src="/icons/icon_back.svg"
              alt="뒤로가기"
              className="w-6 h-6"
            />
          </button>
          <h1 className="text-2xl font-bold text-[#222222]">회원가입</h1>
        </div>

        {/* 단계 표시 */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-[#51C854] rounded-full flex items-center justify-center">
              <span className="text-white font-medium">1</span>
            </div>
            <div className="w-16 h-0.5 bg-[#51C854] mx-2"></div>
            <div className="w-8 h-8 bg-[#51C854] rounded-full flex items-center justify-center">
              <span className="text-white font-medium">2</span>
            </div>
            <div className="w-16 h-0.5 bg-[#DEDEDE] mx-2"></div>
            <div className="w-8 h-8 bg-[#DEDEDE] rounded-full flex items-center justify-center">
              <span className="text-[#999999] font-medium">3</span>
            </div>
          </div>
        </div>

        {/* 입력 폼 */}
        <div className="max-w-[300px] mx-auto space-y-6">
          {/* 업체명 */}
          <div>
            <label className="block text-base font-medium text-[#222222] mb-1">
              업체명
            </label>
            <input
              type="text"
              value={formData.companyName}
              onChange={(e) => handleInputChange('companyName', e.target.value)}
              placeholder="업체명을 입력해주세요"
              className="w-full h-[30px] px-3 text-base bg-[#F5F5F5] border-[1.5px] border-[#51C854] rounded-lg focus:outline-none placeholder:text-[#606060]"
            />
          </div>

          {/* 대표자명 */}
          <div>
            <label className="block text-base font-medium text-[#222222] mb-1">
              대표자명
            </label>
            <input
              type="text"
              value={formData.representative}
              onChange={(e) => handleInputChange('representative', e.target.value)}
              placeholder="대표자명을 입력해주세요"
              className="w-full h-[30px] px-3 text-base bg-[#F5F5F5] border-[1.5px] border-[#51C854] rounded-lg focus:outline-none placeholder:text-[#606060]"
            />
          </div>

          {/* 사업자번호 */}
          <div>
            <label className="block text-base font-medium text-[#222222] mb-1">
              사업자번호
            </label>
            <input
              type="text"
              value={formData.businessNumber}
              onChange={(e) => handleInputChange('businessNumber', e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="사업자번호를 입력해주세요"
              className="w-full h-[30px] px-3 text-base bg-[#F5F5F5] border-[1.5px] border-[#51C854] rounded-lg focus:outline-none placeholder:text-[#606060]"
            />
          </div>

          {/* 은행명 */}
          <div>
            <label className="block text-base font-medium text-[#222222] mb-1">
              은행명
            </label>
            <input
              type="text"
              value={formData.bankName}
              onChange={(e) => handleInputChange('bankName', e.target.value)}
              placeholder="은행명을 입력해주세요"
              className="w-full h-[30px] px-3 text-base bg-[#F5F5F5] border-[1.5px] border-[#51C854] rounded-lg focus:outline-none placeholder:text-[#606060]"
            />
          </div>

          {/* 계좌번호 */}
          <div>
            <label className="block text-base font-medium text-[#222222] mb-1">
              계좌번호
            </label>
            <input
              type="text"
              value={formData.accountNumber}
              onChange={(e) => handleInputChange('accountNumber', e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="계좌번호를 입력해주세요"
              className="w-full h-[30px] px-3 text-base bg-[#F5F5F5] border-[1.5px] border-[#51C854] rounded-lg focus:outline-none placeholder:text-[#606060]"
            />
          </div>

          {/* 예금주 */}
          <div>
            <label className="block text-base font-medium text-[#222222] mb-1">
              예금주
            </label>
            <input
              type="text"
              value={formData.accountHolder}
              onChange={(e) => handleInputChange('accountHolder', e.target.value)}
              placeholder="예금주를 입력해주세요"
              className="w-full h-[30px] px-3 text-base bg-[#F5F5F5] border-[1.5px] border-[#51C854] rounded-lg focus:outline-none placeholder:text-[#606060]"
            />
          </div>
        </div>

        {/* 약관 동의 */}
        <div className="max-w-[320px] mx-auto mt-8">
          <h3 className="text-lg font-semibold text-[#222222] mb-4">약관 동의</h3>
          
          {/* 전체 동의 */}
          <div className="flex items-center mb-4 p-3 bg-[#F5F5F5] rounded-lg">
            <input
              type="checkbox"
              id="all-agreements"
              checked={agreementData.terms && agreementData.privacy && agreementData.marketing}
              onChange={(e) => handleAllAgreementChange(e.target.checked)}
              className="mr-3"
            />
            <label htmlFor="all-agreements" className="text-base font-medium text-[#222222]">
              전체 동의
            </label>
          </div>

          {/* 개별 약관 */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="terms"
                  checked={agreementData.terms}
                  onChange={(e) => handleAgreementChange('terms', e.target.checked)}
                  className="mr-3"
                />
                <label htmlFor="terms" className="text-sm text-[#222222]">
                  <span className="text-[#51C854]">[필수]</span> 서비스 이용약관
                </label>
              </div>
              <button
                type="button"
                onClick={() => {
                  const title = '서비스이용약관(일반 및 공급기업용)';
                  const encodedTitle = encodeURIComponent(title);
                  window.open(`/mypage/terms-policies/title/${encodedTitle}`, '_blank');
                }}
                className="text-xs text-[#666666] underline hover:text-[#51C854] transition-colors"
              >
                더보기
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="privacy"
                  checked={agreementData.privacy}
                  onChange={(e) => handleAgreementChange('privacy', e.target.checked)}
                  className="mr-3"
                />
                <label htmlFor="privacy" className="text-sm text-[#222222]">
                  <span className="text-[#51C854]">[필수]</span> 개인정보 처리방침
                </label>
              </div>
              <button
                type="button"
                onClick={() => {
                  const title = '개인정보처리방침';
                  const encodedTitle = encodeURIComponent(title);
                  window.open(`/mypage/terms-policies/title/${encodedTitle}`, '_blank');
                }}
                className="text-xs text-[#666666] underline hover:text-[#51C854] transition-colors"
              >
                더보기
              </button>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="marketing"
                checked={agreementData.marketing}
                onChange={(e) => handleAgreementChange('marketing', e.target.checked)}
                className="mr-3"
              />
              <label htmlFor="marketing" className="text-sm text-[#222222]">
                <span className="text-[#999999]">[선택]</span> 마케팅 정보 수신
              </label>
            </div>
          </div>
        </div>

        {/* 다음 단계 버튼 */}
        <div className="max-w-[360px] mx-auto mt-12">
          <button
            onClick={handleNext}
            disabled={!isFormValid()}
            className={`w-full h-[31px] font-semibold text-xl rounded-xl transition-colors ${
              isFormValid() 
                ? 'bg-[#51C854] text-white hover:bg-[#46B649]' 
                : 'bg-[#DEDEDE] text-[#999999] cursor-not-allowed'
            }`}
          >
            다음 단계
          </button>
        </div>
      </PageContainer>

      {/* 토스트 메시지 */}
      {toast.show && (
        <Toast
          message={toast.message}
          isVisible={toast.show}
          onClose={() => setToast({ show: false, message: '' })}
        />
      )}
    </div>
  );
}